import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import Grid from "@mui/material/Grid";
import Link from "@mui/material/Link";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useMutation } from "@tanstack/react-query";
import type { ChangeEvent, FormEvent } from "react";
import { useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Avatar, IconButton, useTheme } from "@mui/material";
import { Image } from "mui-image";
import { Copyright } from "../../../components/Copyright";
import { authenticationService } from "../../../services/authentication";
import type { ChangePasswordParameters } from "../../../services/authentication/types";
import { buildSignInPageUrl } from "../../../utils/BuildURLs";
import { DEFAULT_SCHOOL_LOGO_URL, SCHOOL_BACKGROUND_IMAGE } from "../../../utils/Constants";
import { borderRadiusStandard } from "../../../theme";

export const ChangePasswordPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const [searchParameters] = useSearchParams();
  const emailAddress = searchParameters.get("emailAddress")!;
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);
  const [inputData, setInputData] = useState({
    verificationCode: "",
    password: "",
    repeatPassword: "",
  });

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInputData({
      ...inputData,
      [event.target.name]: event.target.value,
    });
  };

  const validatePassword = () => {
    if (inputData.password !== inputData.repeatPassword) {
      toast.error(t("common.passwords_not_match_message"));
      return false;
    }
    return true;
  };

  const { mutate: changePassword, isLoading: isResettingPassword } = useMutation<unknown, Error, ChangePasswordParameters>(
    ({ emailAddress, verificationCode, newPassword }: ChangePasswordParameters) =>
      authenticationService.changePassword({
        emailAddress,
        verificationCode,
        newPassword,
      }),
  );

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    changePassword(
      {
        emailAddress,
        verificationCode: inputData.verificationCode,
        newPassword: inputData.repeatPassword,
      },
      {
        onSuccess: () => {
          toast.success(t("authentication.change_password_page.password_reset_success_message"));
          const signInPageUrl = buildSignInPageUrl({
            email: emailAddress,
          });
          navigate(signInPageUrl);
        },
      },
    );
  };

  return (
    <Grid container component="main" sx={{ height: "100vh" }}>
      <CssBaseline />
      <Grid
        item
        xs={12}
        sx={{
          backgroundImage: `url(${SCHOOL_BACKGROUND_IMAGE})`,
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          backgroundColor: (t) => (t.palette.mode === "dark" ? t.palette.grey[50] : t.palette.grey[900]),
          backgroundPosition: "center",
        }}
      >
        <Grid container>
          <Grid xs={2} sm={0} />
          <Grid xs={12} sm={8} sx={{ mt: "50px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
            <Box
              sx={{
                my: 8,
                mx: 4,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Avatar sx={{ width: 120, height: 120, m: 1, bgcolor: "secondary.main", border: 5, borderColor: "white" }}>
                <Image style={{ margin: 0, width: "100%", background: "white" }} src={DEFAULT_SCHOOL_LOGO_URL} />
              </Avatar>
              <Typography component="h1" variant="h4" sx={{ color: "white", mb: 3 }} fontWeight="700">
                {t("authentication.change_password_page.title")}
              </Typography>
              <Box component="form" onSubmit={handleSubmit} sx={{ p: 4, borderRadius: borderRadiusStandard, mt: 1, background: "white", textAlign: "center" }}>
                <Typography component="h1" variant="h4" sx={{ mb: 3 }} fontWeight="700">
                  {t("authentication.change_password_page.change_password")}
                </Typography>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="verificationCode"
                  label={t("authentication.change_password_page.verification_code")}
                  name="verificationCode"
                  autoComplete="verification code"
                  autoFocus
                  onChange={handleInputChange}
                  disabled={isResettingPassword}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="password"
                  label={t("authentication.change_password_page.new_password")}
                  name="password"
                  autoComplete="New Password"
                  type={showNewPassword ? "text" : "password"}
                  autoFocus
                  onChange={handleInputChange}
                  disabled={isResettingPassword}
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        edge="end"
                        aria-label="show password"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        onMouseDown={() => setShowNewPassword(true)}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    ),
                  }}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="repeatPassword"
                  label={t("authentication.change_password_page.repeat_password")}
                  name="repeatPassword"
                  autoComplete="Repeat New Password"
                  type={showRepeatPassword ? "text" : "password"}
                  autoFocus
                  onChange={handleInputChange}
                  disabled={isResettingPassword}
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        edge="end"
                        aria-label="repeat new password"
                        onClick={() => setShowRepeatPassword(!showRepeatPassword)}
                        onMouseDown={() => setShowRepeatPassword(true)}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    ),
                  }}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  onClick={(event) => {
                    event.currentTarget?.form?.current?.reportValidity();
                    if (!validatePassword()) event.preventDefault();
                  }}
                  sx={{
                    ":hover": {
                      backgroundColor: theme.palette.ylecolor.main,
                      color: "white",
                      py: "10px",
                    },
                    mt: 3,
                    mb: 2,
                    borderRadius: borderRadiusStandard,
                    backgroundColor: theme.palette.ylecolor.main,
                  }}
                >
                  {t("authentication.change_password_page.change_password")}
                </Button>
                <Grid container>
                  <Grid item xs>
                    <Link href={buildSignInPageUrl({ email: emailAddress })} variant="body2">
                      {t("authentication.change_password_page.remember_password")}
                    </Link>
                  </Grid>
                </Grid>
                <Copyright sx={{ mt: 2 }} />
              </Box>
            </Box>
          </Grid>
          <Grid xs={2} sm={0} />
        </Grid>
      </Grid>
    </Grid>
  );
};
